import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { AlertTriangle, ChevronLeft, Clock, Eye, Map, Navigation, Package, RefreshCw, Truck } from 'lucide-react';
import DeliveryDetailModal from './DeliveryDetailModal';
import PickupDetailModal from './PickupDetailModal';
import SequenceModal from './SequenceModal';
import DeliveriesList from './DeliveriesList';
import PickupsList from './PickupsList';
import { isProviderHighlightedForSequence } from './providerHighlight';
import { socket } from '../../admin/chat/socket';

const API_URL = process.env.REACT_APP_API_URL;
const ROUTE_RECONCILIATION_MS = 60 * 1000;

const DELIVERY_TIME_WINDOWS = {
  1: '1:00 PM - 4:00 PM',
  2: '2:00 PM - 5:00 PM',
  3: '3:00 PM - 6:00 PM',
  4: '4:00 PM - 7:00 PM',
  5: '5:00 PM - 8:00 PM',
  6: '6:00 PM - 9:00 PM',
};

function getDeliveryTimeWindow(slot) {
  return DELIVERY_TIME_WINDOWS[Number(slot)] || null;
}

function getWindowBadgeClass(slot) {
  const slotNumber = Number(slot);
  if (slotNumber <= 2) return 'bg-red-100 text-red-700 border-red-200';
  if (slotNumber === 3) return 'bg-orange-100 text-orange-700 border-orange-200';
  return 'bg-amber-100 text-amber-700 border-amber-200';
}

function needsWindowReview(delivery) {
  if (!delivery?.slot) return false;

  const sequence = Number(delivery.sequence);
  if (!sequence) return true;
  if (Number(delivery.slot) <= 2 && sequence > 5) return true;
  if (Number(delivery.slot) === 3 && sequence > 8) return true;
  return false;
}

function ControlTower({ userId, setActiveComponent, driverLocation }) {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const deliveryMarkersRef = useRef([]);
  const pickupMarkersRef = useRef([]);
  const hasFitNextStopRef = useRef(false);
  const fetchInFlightRef = useRef(false);

  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [pickupOrders, setPickupOrders] = useState([]);

  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [sequencePoint, setSequencePoint] = useState(null);
  const [showDeliveriesList, setShowDeliveriesList] = useState(false);
  const [showPickupsList, setShowPickupsList] = useState(false);
  const [serverDriverLocation, setServerDriverLocation] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const effectiveDriverLocation = driverLocation || serverDriverLocation;

  const nextStop = useMemo(() => {
    const sortByRouteOrder = (a, b) => {
      if (a.sequence && b.sequence) return a.sequence - b.sequence;
      if (a.sequence) return -1;
      if (b.sequence) return 1;
      if (a.slot && b.slot) return a.slot - b.slot;
      if (a.slot) return -1;
      if (b.slot) return 1;
      return a.id - b.id;
    };

    const activePickups = pickupOrders
      .filter((pickup) => ['pending', 'encamino'].includes(pickup.status))
      .sort(sortByRouteOrder);

    if (activePickups.length > 0) {
      return { ...activePickups[0], type: 'pickup' };
    }

    const activeDeliveries = deliveryPoints
      .filter((delivery) => ['pending', 'postergado', 'encamino'].includes(delivery.status))
      .sort(sortByRouteOrder);

    if (activeDeliveries.length > 0) {
      return { ...activeDeliveries[0], type: 'delivery' };
    }

    return null;
  }, [deliveryPoints, pickupOrders]);

  const routeHealth = useMemo(() => {
    const activeDeliveries = deliveryPoints.filter(
      (delivery) => !delivery.is_submitted && ['pending', 'postergado', 'encamino'].includes(delivery.status)
    );

    return {
      withoutSequence: activeDeliveries.filter((delivery) => !delivery.sequence).length,
      withTimeWindow: activeDeliveries.filter((delivery) => delivery.slot).length,
      needsReview: activeDeliveries.filter(needsWindowReview).length,
    };
  }, [deliveryPoints]);

  const providerHighlightedSequenceCount = useMemo(() => deliveryPoints.filter(
    (point) => isProviderHighlightedForSequence(point)
      && point.latitude && point.longitude
  ).length, [deliveryPoints]);

  const fetchData = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    try {
      fetchInFlightRef.current = true;
      const [deliveriesRes, pickupsRes] = await Promise.all([
        axios.get(`${API_URL}/api/delivery_points/driver/${userId}`),
        axios.get(`${API_URL}/api/delivery_orders/driver/${userId}`),
      ]);

      setDeliveryPoints(deliveriesRes.data);
      setPickupOrders(pickupsRes.data);
      setLastUpdate(new Date());
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsLoading(false);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [userId]);

  useEffect(() => {
    if (driverLocation || !userId) return;

    async function fetchLastKnownDriverLocation() {
      try {
        const response = await axios.get(`${API_URL}/api/location/driver`, {
          params: { driver_id: userId },
        });

        if (response.data?.latitude && response.data?.longitude) {
          setServerDriverLocation({
            latitude: response.data.latitude,
            longitude: response.data.longitude,
          });
        }
      } catch (error) {
        console.error('Error fetching driver location:', error);
      }
    }

    fetchLastKnownDriverLocation();
  }, [driverLocation, userId]);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current).setView([-12.0464, -77.0428], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let refreshTimer = null;
    const scheduleRefresh = () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(fetchData, 300);
    };
    const handleDeliveryUpdate = payload => {
      if (Number(payload?.driver_id) === Number(userId)) scheduleRefresh();
    };
    const handleOrderUpdate = payload => {
      if (Number(payload?.driver_id) === Number(userId)) scheduleRefresh();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    const reconcile = () => {
      if (document.visibilityState === 'visible') fetchData();
    };

    fetchData();
    const interval = setInterval(reconcile, ROUTE_RECONCILIATION_MS);
    document.addEventListener('visibilitychange', handleVisibility);
    socket.on('connect', fetchData);
    socket.on('delivery_point_updated', handleDeliveryUpdate);
    socket.on('order_point_updated', handleOrderUpdate);

    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      socket.off('connect', fetchData);
      socket.off('delivery_point_updated', handleDeliveryUpdate);
      socket.off('order_point_updated', handleOrderUpdate);
    };
  }, [fetchData, userId]);

  const hasActivePickups = useCallback(() => {
    return pickupOrders.some((pickup) => pickup.status === 'pending' || pickup.status === 'encamino');
  }, [pickupOrders]);

  const isNextStop = useCallback((point, type) => {
    return nextStop?.type === type && nextStop?.id === point.id;
  }, [nextStop]);

  function openNavigationApp(app, stop) {
    if (!stop?.latitude || !stop?.longitude) return;

    const destination = `${stop.latitude},${stop.longitude}`;
    const url =
      app === 'waze'
        ? `https://waze.com/ul?ll=${destination}&navigate=yes`
        : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function openNextStopDetails() {
    if (!nextStop) return;

    if (nextStop.type === 'pickup') {
      setSelectedPickup(nextStop);
      return;
    }

    setSelectedDelivery(nextStop);
  }

  function getNextStopTitle(stop) {
    if (!stop) return '';
    return stop.type === 'pickup' ? 'Siguiente recojo' : 'Siguiente entrega';
  }

  useEffect(() => {
    if (!mapRef.current) return;

    pickupMarkersRef.current.forEach((marker) => marker.remove());

    const newMarkers = pickupOrders
      .filter((pickup) => pickup.latitude && pickup.longitude)
      .map((pickup) => {
        let color = '#95A5A6';

        switch (pickup.status) {
          case 'pending':
            color = '#FF6B35';
            break;
          case 'encamino':
            color = '#4A90E2';
            break;
          case 'recogido':
            color = '#67A43C';
            break;
          case 'cancelado':
            color = '#E74C3C';
            break;
          default:
            color = '#95A5A6';
        }

        const isNext = isNextStop(pickup, 'pickup');
        const icon = L.divIcon({
          html: `<div style="position: relative;">
            ${isNext ? '<span class="ct-pulse-ring ct-pulse-pickup"></span>' : ''}
            <div style="
              background-color: ${color};
              width: ${isNext ? '36px' : '28px'};
              height: ${isNext ? '36px' : '28px'};
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: ${isNext ? '13px' : '11px'};
              color: white;
              font-weight: bold;
              position: relative;
              z-index: 2;
            ">${pickup.sequence || 'P'}</div>
          </div>`,
          className: '',
          iconSize: isNext ? [36, 36] : [28, 28],
          iconAnchor: isNext ? [18, 18] : [14, 14],
        });

        return L.marker([pickup.latitude, pickup.longitude], {
          icon,
          title: `Recojo - ${pickup.customer_name || 'N/A'}`,
        })
          .on('click', () => {
            if (pickup.status === 'pending') {
              setSequencePoint({ ...pickup, type: 'pickup' });
              return;
            }

            setSelectedPickup(pickup);
          })
          .addTo(mapRef.current);
      });

    pickupMarkersRef.current = newMarkers;
  }, [pickupOrders, isNextStop]);

  useEffect(() => {
    if (!mapRef.current) return;

    deliveryMarkersRef.current.forEach((marker) => marker.remove());

    if (hasActivePickups()) {
      deliveryMarkersRef.current = [];
      return;
    }

    const newMarkers = deliveryPoints
      .filter((point) => point.latitude && point.longitude)
      .map((point) => {
        let color = '#95A5A6';

        switch (point.status) {
          case 'pending':
          case 'postergado':
            switch (point.slot) {
              case 1:
                color = '#C62828';
                break;
              case 2:
                color = '#E53935';
                break;
              case 3:
                color = '#F57C00';
                break;
              case 4:
                color = '#FBC02D';
                break;
              case 5:
                color = '#AFB42B';
                break;
              case 6:
                color = '#43A047';
                break;
              case 7:
                color = '#00ACC1';
                break;
              case 8:
                color = '#1E88E5';
                break;
              case 9:
                color = '#5E35B1';
                break;
              default:
                color = '#95A5A6';
            }
            break;
          case 'encamino':
            color = '#4A90E2';
            break;
          case 'entregado':
            color = '#67A43C';
            break;
          case 'cancelado':
            color = '#E74C3C';
            break;
          case 'rechazado':
            color = '#C0392B';
            break;
          default:
            color = '#95A5A6';
        }

        const isNext = isNextStop(point, 'delivery');
        const isProviderHighlighted = isProviderHighlightedForSequence(point);
        const timeWindow = getDeliveryTimeWindow(point.slot);
        const showWindowAlert = needsWindowReview(point);
        const markerSize = isNext ? 34 : 24;
        const timeWindowTop = markerSize + (isProviderHighlighted ? 7 : 1);
        const icon = L.divIcon({
          html: `<div style="position: relative; width: ${markerSize}px; height: ${markerSize}px;">
            ${isNext ? '<span class="ct-pulse-ring ct-pulse-delivery"></span>' : ''}
            ${isProviderHighlighted ? '<span class="ct-provider-halo"></span>' : ''}
            <div style="
              background-color: ${color};
              width: ${markerSize}px;
              height: ${markerSize}px;
              border: ${timeWindow ? '3px solid white' : '2px solid white'};
              border-radius: 50%;
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: ${isNext ? '12px' : '10px'};
              color: white;
              font-weight: bold;
              position: relative;
              z-index: 2;
            ">${point.sequence || ''}</div>
            ${timeWindow ? `
              <div style="
                position: absolute;
                left: 50%;
                top: ${timeWindowTop}px;
                transform: translateX(-50%);
                background: ${showWindowAlert ? '#FEE2E2' : '#FFFFFF'};
                color: ${showWindowAlert ? '#B91C1C' : '#334155'};
                border: 1px solid ${showWindowAlert ? '#FCA5A5' : '#CBD5E1'};
                border-radius: 999px;
                padding: 1px 5px;
                font-size: 9px;
                font-weight: 800;
                white-space: nowrap;
                z-index: 3;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
              ">${timeWindow.replace(':00 ', '').replace(':00 ', '')}</div>
            ` : ''}
          </div>`,
          className: '',
          iconSize: timeWindow ? [72, timeWindowTop + 17] : [markerSize, markerSize],
          iconAnchor: [markerSize / 2, markerSize / 2],
        });

        return L.marker([point.latitude, point.longitude], {
          icon,
          title: `Entrega - ${point.recipient_name || 'N/A'}${isProviderHighlighted ? ' - Vinculada a proveedor' : ''}`,
        })
          .on('click', () => {
            if (['pending', 'postergado'].includes(point.status)) {
              setSequencePoint({ ...point, type: 'delivery' });
              return;
            }

            setSelectedDelivery(point);
          })
          .addTo(mapRef.current);
      });

    deliveryMarkersRef.current = newMarkers;
  }, [deliveryPoints, hasActivePickups, isNextStop]);

  useEffect(() => {
    if (!mapRef.current || !effectiveDriverLocation?.latitude || !effectiveDriverLocation?.longitude) return;

    const driverIcon = L.divIcon({
      className: 'custom-driver-marker',
      html: `
        <img
          src="${process.env.PUBLIC_URL}/logo.png"
          alt="Fast Max Driver"
          style="
            width: 40px;
            height: 40px;
            object-fit: contain;
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
          "
        />
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const position = [effectiveDriverLocation.latitude, effectiveDriverLocation.longitude];

    if (driverMarkerRef.current) {
      driverMarkerRef.current.setLatLng(position);
      return;
    }

    driverMarkerRef.current = L.marker(position, {
      icon: driverIcon,
      title: 'Mi ubicacion',
    }).addTo(mapRef.current);
  }, [effectiveDriverLocation]);

  useEffect(() => {
    if (!mapRef.current || !nextStop?.latitude || !nextStop?.longitude || hasFitNextStopRef.current) {
      return;
    }

    const points = [[nextStop.latitude, nextStop.longitude]];

    if (effectiveDriverLocation?.latitude && effectiveDriverLocation?.longitude) {
      points.push([effectiveDriverLocation.latitude, effectiveDriverLocation.longitude]);
    }

    mapRef.current.fitBounds(L.latLngBounds(points), { padding: [70, 70], maxZoom: 15 });
    hasFitNextStopRef.current = true;
  }, [nextStop, effectiveDriverLocation]);

  async function handleSequenceUpdate(sequence) {
    if (!sequencePoint) return;

    try {
      const endpoint = sequencePoint.type === 'pickup'
        ? '/api/change/order_sequence'
        : '/api/change/sequence';

      await axios.post(`${API_URL}${endpoint}`, {
        id: sequencePoint.id,
        sequence: parseInt(sequence, 10),
      });

      await fetchData();
      setSequencePoint(null);
    } catch (error) {
      console.error('Error updating sequence:', error);
      alert('Error al actualizar la secuencia');
    }
  }

  function handleDeliveryClick(delivery) {
    setShowDeliveriesList(false);
    if (['pending', 'postergado'].includes(delivery.status)) {
      setSequencePoint({ ...delivery, type: 'delivery' });
      return;
    }

    setSelectedDelivery(delivery);
  }

  function handlePickupClick(pickup) {
    setShowPickupsList(false);
    if (pickup.status === 'pending') {
      setSequencePoint({ ...pickup, type: 'pickup' });
      return;
    }

    setSelectedPickup(pickup);
  }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveComponent('OptionsDriver')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-lg sm:text-xl font-extrabold text-[#B1336E] font-montserrat">
                  Torre de Control
                </h1>
                <p className="text-xs text-slate-600 font-montserrat">
                  Actualizado: {lastUpdate.toLocaleTimeString('es-PE')}
                </p>
              </div>
            </div>

            <button
              onClick={fetchData}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <RefreshCw className={`w-5 h-5 text-slate-700 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-2">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-2 sm:gap-4 justify-center items-center">
          <button
            onClick={() => setShowDeliveriesList(true)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#B1336E]/10 to-[#E6B43C]/10 rounded-lg hover:from-[#B1336E]/20 hover:to-[#E6B43C]/20 transition-all active:scale-95"
          >
            <Truck className="w-4 h-4 text-[#B1336E]" />
            <span className="text-xs font-montserrat font-semibold text-slate-700">
              Entregas: <span className="text-[#B1336E]">{deliveryPoints.length}</span>
            </span>
          </button>
          <button
            onClick={() => setShowPickupsList(true)}
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#67A43C]/10 to-[#E6B43C]/10 rounded-lg hover:from-[#67A43C]/20 hover:to-[#E6B43C]/20 transition-all active:scale-95"
          >
            <Package className="w-4 h-4 text-[#67A43C]" />
            <span className="text-xs font-montserrat font-semibold text-slate-700">
              Recojos: <span className="text-[#67A43C]">{pickupOrders.length}</span>
            </span>
          </button>
          {providerHighlightedSequenceCount > 0 && !hasActivePickups() && (
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-teal-800 font-montserrat">
              <span className="w-5 h-5 rounded-full border-2 border-teal-700 ring-2 ring-teal-100 shadow-[0_0_8px_rgba(13,148,136,0.65)]" aria-hidden="true" />
              Vinculada a proveedor ({providerHighlightedSequenceCount})
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 relative">
        <div ref={mapContainerRef} className="absolute inset-0" />

        {nextStop && (
          <div className="absolute top-4 left-4 right-4 z-[1000] sm:left-auto sm:right-4 sm:max-w-sm">
            <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase font-bold text-slate-500 font-montserrat">
                  {getNextStopTitle(nextStop)}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`inline-flex items-center justify-center min-w-8 h-8 px-2 rounded-full text-xs font-extrabold text-white font-montserrat ${
                      nextStop.type === 'pickup' ? 'bg-[#67A43C]' : 'bg-[#B1336E]'
                    }`}
                  >
                    #{nextStop.sequence || '-'}
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-sm font-extrabold text-slate-900 font-montserrat truncate">
                      {nextStop.type === 'pickup'
                        ? nextStop.customer_name || 'Recojo sin nombre'
                        : nextStop.recipient_name || 'Entrega sin nombre'}
                    </h2>
                    {nextStop.type === 'delivery' && nextStop.slot && (
                      <span
                        className={`inline-flex mt-1 px-2 py-0.5 rounded-full border text-[11px] font-bold font-montserrat ${
                          getWindowBadgeClass(nextStop.slot)
                        }`}
                      >
                        {getDeliveryTimeWindow(nextStop.slot)}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-600 font-montserrat mt-2 line-clamp-2">
                  {nextStop.address || 'Sin direccion'}
                  {nextStop.district ? `, ${nextStop.district}` : ''}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3">
                <button
                  onClick={() => openNavigationApp('google', nextStop)}
                  disabled={!nextStop.latitude || !nextStop.longitude}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-[#B1336E] text-white text-xs font-bold font-montserrat disabled:bg-slate-300 disabled:text-slate-500"
                >
                  <Map className="w-3.5 h-3.5" />
                  Google
                </button>
                <button
                  onClick={() => openNavigationApp('waze', nextStop)}
                  disabled={!nextStop.latitude || !nextStop.longitude}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-[#4A90E2] text-white text-xs font-bold font-montserrat disabled:bg-slate-300 disabled:text-slate-500"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Waze
                </button>
                <button
                  onClick={openNextStopDetails}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold font-montserrat"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Ver
                </button>
              </div>
            </div>
          </div>
        )}

        {(routeHealth.withTimeWindow > 0 || routeHealth.withoutSequence > 0) && (
          <div className="absolute left-4 right-4 bottom-4 z-[1000] sm:right-auto sm:max-w-sm">
            <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#B1336E]" />
                <p className="text-xs font-extrabold text-slate-900 font-montserrat">
                  Ayuda de ruta
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-slate-50 px-2 py-2">
                  <p className="text-base font-extrabold text-slate-900 font-montserrat">{routeHealth.withTimeWindow}</p>
                  <p className="text-[10px] text-slate-500 font-montserrat">con horario</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-2 py-2">
                  <p className="text-base font-extrabold text-slate-900 font-montserrat">{routeHealth.withoutSequence}</p>
                  <p className="text-[10px] text-slate-500 font-montserrat">sin orden</p>
                </div>
                <div className={`rounded-lg px-2 py-2 ${routeHealth.needsReview ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className={`text-base font-extrabold font-montserrat ${routeHealth.needsReview ? 'text-red-700' : 'text-green-700'}`}>
                    {routeHealth.needsReview}
                  </p>
                  <p className={`text-[10px] font-montserrat ${routeHealth.needsReview ? 'text-red-600' : 'text-green-600'}`}>
                    revisar
                  </p>
                </div>
              </div>
              {routeHealth.needsReview > 0 && (
                <div className="flex items-start gap-2 mt-2 rounded-lg bg-red-50 px-2 py-2 text-xs text-red-700 font-montserrat">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Hay entregas con ventana horaria que necesitan orden o estan muy tarde.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {hasActivePickups() && (
          <div className="absolute bottom-40 left-1/2 -translate-x-1/2 z-[1000] max-w-sm">
            <div className="bg-gradient-to-r from-[#FF6B35] to-[#E65100] text-white px-4 py-2 rounded-lg shadow-lg">
              <p className="text-sm font-montserrat font-semibold text-center">
                Recojos pendientes - Las entregas se mostraran despues
              </p>
            </div>
          </div>
        )}
      </div>

      {selectedDelivery && (
        <DeliveryDetailModal delivery={selectedDelivery} onClose={() => setSelectedDelivery(null)} />
      )}

      {selectedPickup && (
        <PickupDetailModal pickup={selectedPickup} onClose={() => setSelectedPickup(null)} />
      )}

      {sequencePoint && (
        <SequenceModal
          point={sequencePoint}
          onClose={() => setSequencePoint(null)}
          onSubmit={handleSequenceUpdate}
        />
      )}

      {showDeliveriesList && (
        <DeliveriesList
          deliveries={deliveryPoints}
          onClose={() => setShowDeliveriesList(false)}
          onDeliveryClick={handleDeliveryClick}
        />
      )}

      {showPickupsList && (
        <PickupsList
          pickups={pickupOrders}
          onClose={() => setShowPickupsList(false)}
          onPickupClick={handlePickupClick}
        />
      )}

      <style>{`
        .ct-pulse-ring {
          position: absolute;
          inset: -8px;
          border-radius: 9999px;
          opacity: 0.8;
          animation: ctPulse 1.4s ease-out infinite;
          z-index: 1;
        }

        .ct-pulse-pickup {
          border: 3px solid #67A43C;
        }

        .ct-pulse-delivery {
          border: 3px solid #B1336E;
        }

        .ct-provider-halo {
          position: absolute;
          inset: -5px;
          border: 2px solid #0F766E;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.95), 0 0 10px 4px rgba(13,148,136,0.7);
          pointer-events: none;
          z-index: 1;
        }

        @keyframes ctPulse {
          0% {
            transform: scale(0.72);
            opacity: 0.9;
          }
          100% {
            transform: scale(1.45);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default ControlTower;
